/**
 * PATH: packages/ui/src/components/settings/security-tab/AccountSwitcherSection.tsx
 * PURPOSE: Settings UI for the multi-account vault — list saved logins,
 *          switch between them, add another account, remove entries.
 * WHY: Client ask (2026-07): "Allow clear session management and multi
 *      account login". The vault mechanics live in @quotech/auth
 *      (accountSwitcher.ts); this renders them.
 * FLOW:
 *   ┌──────────────────┐   ┌────────────────┐   ┌────────────────────┐
 *   │ snapshot current │ → │ list accounts  │ → │ switch / add /     │
 *   │ on mount         │   │ (active badge) │   │ remove             │
 *   └──────────────────┘   └────────────────┘   └────────────────────┘
 * DEPENDENCIES:
 *   - vault prop: implemented by @quotech/auth accountSwitcher, injected
 *     by the host app. Injected (not imported) because tenant SPAs like
 *     apps/admin compile @quotech/ui WITHOUT @quotech/auth — a static
 *     import here breaks their typecheck gate.
 */

import { useCallback, useEffect, useState } from 'react'
import { Users, Plus, LogIn, X } from 'lucide-react'

export interface SavedAccountSummary {
  id: string
  email: string
  name: string
  orgName: string | null
  savedAt: string
}

/** The @quotech/auth accountSwitcher surface this section renders. */
export interface AccountVault {
  snapshotCurrentAccount: (user?: { id?: string | number; email?: string; name?: string } | null) => unknown
  listSavedAccounts: () => SavedAccountSummary[]
  getActiveAccountId: () => string | null
  switchToAccount: (id: string, user?: { id?: string | number; email?: string; name?: string } | null) => boolean
  removeSavedAccount: (id: string) => void
  beginAddAccount: (user?: { id?: string | number; email?: string; name?: string } | null, loginPath?: string) => void
}

export interface AccountSwitcherSectionProps {
  /** Vault implementation — pass the functions from @quotech/auth. */
  vault: AccountVault
  /** Current user — improves the vault entry's display name/email. */
  user?: { id?: string | number; email?: string; name?: string } | null
  /** Where "Add another account" sends the user (defaults to /login). */
  loginPath?: string
}

export function AccountSwitcherSection({ vault, user, loginPath = '/login' }: AccountSwitcherSectionProps) {
  const {
    snapshotCurrentAccount,
    listSavedAccounts,
    getActiveAccountId,
    switchToAccount,
    removeSavedAccount,
    beginAddAccount,
  } = vault
  const [accounts, setAccounts] = useState<SavedAccountSummary[]>([])
  const [activeId, setActiveId] = useState<string | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    // Ensure the current login is always in the vault before listing.
    snapshotCurrentAccount(user ?? undefined)
    setAccounts(listSavedAccounts())
    setActiveId(getActiveAccountId())
  }, [user, vault])

  const handleSwitch = useCallback(
    (id: string) => {
      setError('')
      const ok = switchToAccount(id, user ?? undefined)
      if (!ok) {
        setError('That account is no longer saved on this device — sign in again to add it.')
        setAccounts(listSavedAccounts())
      }
    },
    [user, vault],
  )

  const handleRemove = useCallback((id: string) => {
    removeSavedAccount(id)
    setAccounts(listSavedAccounts())
  }, [vault])

  const handleAdd = useCallback(() => {
    beginAddAccount(user ?? undefined, loginPath)
  }, [user, loginPath, vault])

  return (
    <section>
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 rounded-lg bg-muted">
          <Users className="w-5 h-5 text-muted-foreground" />
        </div>
        <div>
          <h3 className="font-semibold text-foreground">Accounts on this device</h3>
          <p className="text-sm text-muted-foreground">
            Switch between saved logins without re-entering your password.
          </p>
        </div>
      </div>

      <div className="divide-y divide-border rounded-xl border border-border overflow-hidden">
        {accounts.length === 0 && (
          <div className="p-4 text-sm text-muted-foreground">No saved accounts yet.</div>
        )}
        {accounts.map((account) => {
          const isActive = account.id === activeId
          return (
            <div key={account.id} className="flex items-center gap-3 p-3 bg-background">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-foreground truncate">
                  {account.name || account.email}
                  {isActive && (
                    <span className="ml-2 px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-xs font-medium">
                      current
                    </span>
                  )}
                </p>
                <p className="text-xs text-muted-foreground truncate">
                  {account.email}
                  {account.orgName ? ` · ${account.orgName}` : ''}
                </p>
              </div>
              {!isActive && (
                <button
                  onClick={() => handleSwitch(account.id)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-medium"
                >
                  <LogIn className="w-3.5 h-3.5" /> Switch
                </button>
              )}
              {!isActive && (
                <button
                  onClick={() => handleRemove(account.id)}
                  className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground"
                  aria-label={`Remove ${account.email}`}
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          )
        })}
      </div>

      <button
        onClick={handleAdd}
        className="mt-3 inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-border text-sm font-medium"
      >
        <Plus className="w-4 h-4" /> Add another account
      </button>

      {error && <p className="text-sm text-destructive mt-2">{error}</p>}

      <p className="text-xs text-muted-foreground mt-3">
        Saved logins stay on this device only. Revoking a session from the
        Security section also signs that saved account out everywhere.
      </p>
    </section>
  )
}

export default AccountSwitcherSection
